import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardBody,
  Switch,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  Button,
  useToast,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  Code,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  FormControl,
  FormLabel,
  Input,
  Wrap,
  WrapItem
} from '@chakra-ui/react';

const AdminAICapabilities = ({ standalone = false }) => {
  const [aiCapabilities, setAiCapabilities] = useState({});
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState({});
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [capabilityDetails, setCapabilityDetails] = useState({});
  const [validationData, setValidationData] = useState(null);
  const [loadingValidation, setLoadingValidation] = useState(false);
  const [aiReasoningTools, setAiReasoningTools] = useState(null);

  const Wrapper = React.Fragment;

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    loadAICapabilities();
    loadFeatures();
    loadValidationData();
    loadAIReasoningTools();
  }, []);

  const loadAICapabilities = async () => {
    try {
      const response = await fetch('/api/core-features-registry/ai-capabilities', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAiCapabilities(data.ai_capabilities || {});
      } else {
        throw new Error('Failed to load AI capabilities');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const loadFeatures = async () => {
    try {
      const response = await fetch('/api/core-features-registry/features', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFeatures(data.features || {});
      } else {
        throw new Error('Failed to load features');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadValidationData = async () => {
    setLoadingValidation(true);
    try {
      const response = await fetch('/api/capability-validation/validate-all', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setValidationData(data);
      } else {
        console.warn('Failed to load validation data');
      }
    } catch (err) {
      console.warn('Error loading validation data:', err);
    } finally {
      setLoadingValidation(false);
    }
  };

  const loadAIReasoningTools = async () => {
    try {
      const response = await fetch('/api/core-features-registry/ai-reasoning-tools', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAiReasoningTools(data.ai_reasoning_tools || {});
      } else {
        console.warn('Failed to load AI reasoning tools');
      }
    } catch (err) {
      console.warn('Error loading AI reasoning tools:', err);
    }
  };

  const loadAgentCapabilityReport = async () => {
    try {
      const response = await fetch('/api/agents/capability-validation-report', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }
    } catch (err) {
      console.warn('Error loading agent capability report:', err);
    }
    return null;
  };

  const updateCapabilityStatus = async (featureId, capabilityId, enabled) => {
    const updateKey = `${featureId}_${capabilityId}`;
    setUpdating(prev => ({ ...prev, [updateKey]: true }));

    try {
      const response = await fetch(`/api/core-features-registry/ai-capabilities/${featureId}/${capabilityId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ enabled })
      });

      if (response.ok) {
        const result = await response.json();

        // Update local state
        setAiCapabilities(prev => ({
          ...prev,
          [featureId]: {
            ...prev[featureId],
            capabilities: {
              ...prev[featureId].capabilities,
              [capabilityId]: {
                ...prev[featureId].capabilities[capabilityId],
                enabled
              }
            }
          }
        }));

        toast({
          title: 'Capability Updated',
          description: `${capabilityId} ${enabled ? 'enabled' : 'disabled'} for ${featureId}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error('Failed to update capability');
      }
    } catch (err) {
      toast({
        title: 'Update Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(prev => ({ ...prev, [updateKey]: false }));
    }
  };

  const enableAllCapabilities = async () => {
    setBulkUpdating(true);
    let successCount = 0;
    let errorCount = 0;

    for (const [featureId, featureData] of Object.entries(aiCapabilities)) {
      for (const [capabilityId, capability] of Object.entries(featureData.capabilities)) {
        if (!capability.enabled) {
          try {
            await updateCapabilityStatus(featureId, capabilityId, true);
            successCount++;
          } catch (err) {
            errorCount++;
          }
        }
      }
    }

    setBulkUpdating(false);
    toast({
      title: 'Bulk Update Complete',
      description: `${successCount} capabilities enabled, ${errorCount} errors`,
      status: successCount > 0 ? 'success' : 'warning',
      duration: 5000,
      isClosable: true,
    });
  };

  const disableAllCapabilities = async () => {
    setBulkUpdating(true);
    let successCount = 0;
    let errorCount = 0;

    for (const [featureId, featureData] of Object.entries(aiCapabilities)) {
      for (const [capabilityId, capability] of Object.entries(featureData.capabilities)) {
        if (capability.enabled) {
          try {
            await updateCapabilityStatus(featureId, capabilityId, false);
            successCount++;
          } catch (err) {
            errorCount++;
          }
        }
      }
    }

    setBulkUpdating(false);
    toast({
      title: 'Bulk Update Complete',
      description: `${successCount} capabilities disabled, ${errorCount} errors`,
      status: successCount > 0 ? 'success' : 'warning',
      duration: 5000,
      isClosable: true,
    });
  };

  const openCapabilityDetails = (featureId, capabilityId, capability) => {
    setSelectedFeature(featureId);
    setCapabilityDetails({
      id: capabilityId,
      ...capability,
      featureName: aiCapabilities[featureId]?.feature_name || featureId
    });
    onOpen();
  };

  const getCapabilityStats = () => {
    let totalCapabilities = 0;
    let enabledCapabilities = 0;
    let featuresWithAI = 0;

    // Count component capabilities
    Object.values(aiCapabilities).forEach(feature => {
      if (feature.capabilities && Object.keys(feature.capabilities).length > 0) {
        featuresWithAI++;
        Object.values(feature.capabilities).forEach(capability => {
          totalCapabilities++;
          if (capability.enabled) {
            enabledCapabilities++;
          }
        });
      }
    });

    // Count AI reasoning tools (always enabled)
    const aiToolsCount = Object.keys(aiReasoningTools?.ai_reasoning_tools || {}).length;
    totalCapabilities += aiToolsCount;
    enabledCapabilities += aiToolsCount;

    return {
      totalCapabilities,
      enabledCapabilities,
      disabledCapabilities: totalCapabilities - enabledCapabilities,
      featuresWithAI,
      aiToolsCount,
      enabledPercentage: totalCapabilities > 0 ? (enabledCapabilities / totalCapabilities * 100).toFixed(1) : 0
    };
  };

  const getCapabilityRealityStatus = (capabilityId) => {
    // Check if it's an AI reasoning tool (always enabled and implemented)
    if (aiReasoningTools?.ai_reasoning_tools?.[capabilityId]) {
      return {
        status: 'ai_reasoning_tool',
        implemented: true,
        registry_enabled: true,
        handler_method: 'Backend API',
        is_consistent: true,
        type: 'ai_reasoning_tool'
      };
    }

    if (!validationData?.implementation_details) return null;

    const implStatus = validationData.implementation_details[capabilityId];
    if (!implStatus) return { status: 'unknown', implemented: false, registry_enabled: false };

    // Find if this capability is enabled in registry
    let registryEnabled = false;
    Object.values(aiCapabilities).forEach(feature => {
      if (feature.capabilities?.[capabilityId]?.enabled) {
        registryEnabled = true;
      }
    });

    return {
      status: implStatus.implemented ? 'implemented' : 'not_implemented',
      implemented: implStatus.implemented,
      registry_enabled: registryEnabled,
      handler_method: implStatus.handler_method,
      is_consistent: registryEnabled === implStatus.implemented,
      type: 'component_capability'
    };
  };

  if (loading) {
    return (
      <Wrapper>
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <VStack>
            <Spinner size="xl" />
            <Text>Loading AI capabilities...</Text>
          </VStack>
        </Box>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <Alert status="error">
          <AlertIcon />
          Error loading AI capabilities: {error}
        </Alert>
      </Wrapper>
    );
  }

  const stats = getCapabilityStats();

  return (
    <Wrapper>
      <Box width="100%" mx="auto" bg="white" p={6}>
        <VStack align="flex-start" spacing={6} width="100%">
          <Box>
            <Heading size="xl" mb={2}>AI Tools & Capabilities Management</Heading>
            <Text color="gray.600">
              Control which AI capabilities are enabled for each feature component
            </Text>
          </Box>

          <Tabs width="100%" colorScheme="blue">
            <TabList>
              <Tab>Overview & Controls</Tab>
              <Tab>Feature Capabilities</Tab>
              <Tab>AI Reasoning Tools</Tab>
              <Tab>Capability Matrix</Tab>
              <Tab>Reality Check</Tab>
              <Tab>Statistics</Tab>
            </TabList>

            <TabPanels>
              {/* Overview & Controls Tab */}
              <TabPanel>
                <VStack align="start" spacing={6} width="100%">
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} width="100%">
                    <Card>
                      <CardBody>
                        <Stat>
                          <StatLabel>Total Capabilities</StatLabel>
                          <StatNumber>{stats.totalCapabilities}</StatNumber>
                          <StatHelpText>Across all features</StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Stat>
                          <StatLabel>Enabled</StatLabel>
                          <StatNumber color="green.500">{stats.enabledCapabilities}</StatNumber>
                          <StatHelpText>{stats.enabledPercentage}% active</StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Stat>
                          <StatLabel>Disabled</StatLabel>
                          <StatNumber color="red.500">{stats.disabledCapabilities}</StatNumber>
                          <StatHelpText>Not active</StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Stat>
                          <StatLabel>AI Tools</StatLabel>
                          <StatNumber>{stats.aiToolsCount}</StatNumber>
                          <StatHelpText>Backend reasoning tools</StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  <Card width="100%">
                    <CardBody>
                      <VStack align="start" spacing={4}>
                        <Heading size="md">Bulk Actions</Heading>
                        <Text color="gray.600">
                          Quickly enable or disable all AI capabilities across the platform
                        </Text>
                        <HStack spacing={4}>
                          <Button
                            colorScheme="green"
                            onClick={enableAllCapabilities}
                            isLoading={bulkUpdating}
                            loadingText="Enabling..."
                          >
                            Enable All Capabilities
                          </Button>
                          <Button
                            colorScheme="red"
                            variant="outline"
                            onClick={disableAllCapabilities}
                            isLoading={bulkUpdating}
                            loadingText="Disabling..."
                          >
                            Disable All Capabilities
                          </Button>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>
                </VStack>
              </TabPanel>

              {/* Feature Capabilities Tab */}
              <TabPanel>
                <VStack align="start" spacing={4} width="100%">
                  <Heading size="lg">Feature-by-Feature Capabilities</Heading>

                  <Accordion allowMultiple width="100%">
                    {Object.entries(aiCapabilities).map(([featureId, featureData]) => (
                      <AccordionItem key={featureId}>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <HStack justify="space-between" width="100%">
                              <VStack align="start" spacing={1}>
                                <Text fontWeight="bold">{featureData.feature_name}</Text>
                                <HStack>
                                  <Code fontSize="xs">{featureData.component}</Code>
                                  <Badge colorScheme="blue">
                                    {Object.keys(featureData.capabilities || {}).length} capabilities
                                  </Badge>
                                </HStack>
                              </VStack>
                              <HStack>
                                <Badge 
                                  colorScheme="green"
                                  variant={Object.values(featureData.capabilities || {}).filter(c => c.enabled).length > 0 ? "solid" : "outline"}
                                >
                                  {Object.values(featureData.capabilities || {}).filter(c => c.enabled).length} enabled
                                </Badge>
                                <Badge 
                                  colorScheme="red"
                                  variant={Object.values(featureData.capabilities || {}).filter(c => !c.enabled).length > 0 ? "solid" : "outline"}
                                >
                                  {Object.values(featureData.capabilities || {}).filter(c => !c.enabled).length} disabled
                                </Badge>
                              </HStack>
                            </HStack>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb={4}>
                          <VStack align="start" spacing={3}>
                            {Object.entries(featureData.capabilities || {}).map(([capabilityId, capability]) => (
                              <Card key={capabilityId} width="100%" variant="outline">
                                <CardBody>
                                  <HStack justify="space-between" align="start">
                                    <VStack align="start" spacing={2} flex="1">
                                      <HStack>
                                        <Text fontWeight="semibold">{capabilityId}</Text>
                                        <Badge colorScheme={capability.enabled ? "green" : "gray"}>
                                          {capability.enabled ? "Enabled" : "Disabled"}
                                        </Badge>
                                      </HStack>
                                      <Text fontSize="sm" color="gray.600">
                                        {capability.description}
                                      </Text>
                                      {capability.usage && (
                                        <Text fontSize="xs" color="blue.600" fontStyle="italic">
                                          Example: "{capability.usage}"
                                        </Text>
                                      )}
                                    </VStack>
                                    <VStack spacing={2}>
                                      <Switch
                                        isChecked={capability.enabled}
                                        onChange={(e) => updateCapabilityStatus(featureId, capabilityId, e.target.checked)}
                                        isDisabled={updating[`${featureId}_${capabilityId}`]}
                                        colorScheme="green"
                                      />
                                      <Button
                                        size="xs"
                                        variant="outline"
                                        onClick={() => openCapabilityDetails(featureId, capabilityId, capability)}
                                      >
                                        Details
                                      </Button>
                                    </VStack>
                                  </HStack>
                                </CardBody>
                              </Card>
                            ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </VStack>
              </TabPanel>

              {/* AI Reasoning Tools Tab */}
              <TabPanel>
                <VStack align="start" spacing={4} width="100%">
                  <Heading size="lg">AI Reasoning Tools</Heading>
                  <Text color="gray.600">
                    These are backend AI tools that agents can use to process and analyze data. Unlike component capabilities, these are always enabled and available to all agents.
                  </Text>

                  {!aiReasoningTools ? (
                    <Box display="flex" justifyContent="center" width="100%">
                      <Spinner size="lg" />
                    </Box>
                  ) : (
                    <Accordion allowMultiple width="100%">
                      <AccordionItem>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <HStack justify="space-between" width="100%">
                              <VStack align="start" spacing={1}>
                                <Text fontWeight="bold">AI Reasoning Tools Registry</Text>
                                <HStack>
                                  <Code fontSize="xs">Backend AI Tools</Code>
                                  <Badge colorScheme="purple">
                                    {Object.keys(aiReasoningTools || {}).length} tools
                                  </Badge>
                                </HStack>
                              </VStack>
                              <HStack>
                                <Badge colorScheme="green" variant="solid">
                                  {Object.keys(aiReasoningTools || {}).length} enabled
                                </Badge>
                                <Badge colorScheme="purple" variant="outline">
                                  Always Available
                                </Badge>
                              </HStack>
                            </HStack>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb={4}>
                          <VStack align="start" spacing={3}>
                            {Object.entries(aiReasoningTools || {}).map(([toolId, tool]) => (
                              <Card key={toolId} width="100%" variant="outline">
                                <CardBody>
                                  <HStack justify="space-between" align="start">
                                    <VStack align="start" spacing={2} flex="1">
                                      <HStack>
                                        <Text fontWeight="semibold">{tool.name}</Text>
                                        <Badge colorScheme="purple" variant="solid">AI Tool</Badge>
                                        <Badge colorScheme="green" size="sm">Always Enabled</Badge>
                                      </HStack>
                                      <Text fontSize="sm" color="gray.600">
                                        {tool.description}
                                      </Text>
                                      <VStack align="start" spacing={1} width="100%">
                                        <Text fontSize="xs" fontWeight="semibold">Endpoint:</Text>
                                        <Code fontSize="xs">{tool.method} {tool.endpoint}</Code>
                                      </VStack>
                                      <VStack align="start" spacing={1} width="100%">
                                        <Text fontSize="xs" fontWeight="semibold">Required Parameters:</Text>
                                        <Wrap>
                                          {tool.requiredParams?.map((param) => (
                                            <WrapItem key={param}>
                                              <Badge colorScheme="blue" variant="outline" size="sm">
                                                {param}
                                              </Badge>
                                            </WrapItem>
                                          ))}
                                        </Wrap>
                                      </VStack>
                                      <VStack align="start" spacing={1} width="100%">
                                        <Text fontSize="xs" fontWeight="semibold">Mission Type:</Text>
                                        <Badge colorScheme="green" variant="outline">
                                          {tool.mission_type}
                                        </Badge>
                                      </VStack>
                                    </VStack>
                                    <VStack spacing={2}>
                                      <Badge colorScheme="green" size="lg">Enabled</Badge>
                                      <Button
                                        size="xs"
                                        variant="outline"
                                        onClick={() => openCapabilityDetails('ai_reasoning_tools', toolId, {
                                          enabled: true,
                                          description: tool.description,
                                          usage: `Example: Call ${tool.endpoint} with ${tool.requiredParams?.join(', ')}`
                                        })}
                                      >
                                        Details
                                      </Button>
                                    </VStack>
                                  </HStack>
                                </CardBody>
                              </Card>
                            ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  )}
                </VStack>
              </TabPanel>

              {/* Capability Matrix Tab */}
              <TabPanel>
                <VStack align="start" spacing={4} width="100%">
                  <Heading size="lg">Capability Matrix</Heading>
                  <Text color="gray.600">Overview of all capabilities across features and AI reasoning tools</Text>

                  <Box width="100%" overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Feature/Tool</Th>
                          <Th>Component/Type</Th>
                          <Th>Capability</Th>
                          <Th>Status</Th>
                          <Th>Description</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {/* Component Capabilities */}
                        {Object.entries(aiCapabilities).map(([featureId, featureData]) =>
                          Object.entries(featureData.capabilities || {}).map(([capabilityId, capability]) => (
                            <Tr key={`component_${featureId}_${capabilityId}`}>
                              <Td>
                                <VStack align="start" spacing={1}>
                                  <Text fontSize="sm" fontWeight="semibold">{featureData.feature_name}</Text>
                                  <Code fontSize="xs">{featureId}</Code>
                                </VStack>
                              </Td>
                              <Td>
                                <VStack align="start" spacing={1}>
                                  <Code fontSize="xs">{featureData.component}</Code>
                                  <Badge colorScheme="blue" size="xs">Component</Badge>
                                </VStack>
                              </Td>
                              <Td>
                                <Text fontSize="sm" fontWeight="medium">{capabilityId}</Text>
                              </Td>
                              <Td>
                                <Badge colorScheme={capability.enabled ? "green" : "red"}>
                                  {capability.enabled ? "Enabled" : "Disabled"}
                                </Badge>
                              </Td>
                              <Td maxWidth="300px">
                                <Text fontSize="xs" noOfLines={2}>
                                  {capability.description}
                                </Text>
                              </Td>
                              <Td>
                                <HStack spacing={2}>
                                  <Switch
                                    size="sm"
                                    isChecked={capability.enabled}
                                    onChange={(e) => updateCapabilityStatus(featureId, capabilityId, e.target.checked)}
                                    isDisabled={updating[`${featureId}_${capabilityId}`]}
                                    colorScheme="green"
                                  />
                                  {updating[`${featureId}_${capabilityId}`] && <Spinner size="xs" />}
                                </HStack>
                              </Td>
                            </Tr>
                          ))
                        )}
                        
                        {/* AI Reasoning Tools */}
                        {Object.entries(aiReasoningTools?.ai_reasoning_tools || {}).map(([toolId, tool]) => (
                          <Tr key={`ai_tool_${toolId}`} bg="purple.50">
                            <Td>
                              <VStack align="start" spacing={1}>
                                <Text fontSize="sm" fontWeight="semibold">{tool.name}</Text>
                                <Code fontSize="xs">{toolId}</Code>
                              </VStack>
                            </Td>
                            <Td>
                              <VStack align="start" spacing={1}>
                                <Code fontSize="xs">Backend API</Code>
                                <Badge colorScheme="purple" size="xs">AI Tool</Badge>
                              </VStack>
                            </Td>
                            <Td>
                              <Text fontSize="sm" fontWeight="medium">{toolId}</Text>
                            </Td>
                            <Td>
                              <Badge colorScheme="green">Always Enabled</Badge>
                            </Td>
                            <Td maxWidth="300px">
                              <Text fontSize="xs" noOfLines={2}>
                                {tool.description}
                              </Text>
                            </Td>
                            <Td>
                              <HStack spacing={2}>
                                <Badge colorScheme="purple" size="sm" variant="outline">
                                  System Tool
                                </Badge>
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Reality Check Tab */}
              <TabPanel>
                <VStack align="start" spacing={6} width="100%">
                  <Heading size="lg">Capability Reality Check</Heading>
                  <Text color="gray.600">
                    Compare registry status vs actual implementation status and agent capabilities
                  </Text>

                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold">Multi-Layer Validation</Text>
                      <Text fontSize="sm">
                        This validates capabilities across three layers: Registry definitions, ComponentActionHandler implementation, and Agent configurations.
                      </Text>
                    </VStack>
                  </Alert>

                  {loadingValidation && (
                    <Box display="flex" justifyContent="center" width="100%">
                      <Spinner size="lg" />
                    </Box>
                  )}

                  {validationData && (
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} width="100%">
                      <Card>
                        <CardBody>
                          <Stat>
                            <StatLabel>Implementation Gaps</StatLabel>
                            <StatNumber color="orange.500">
                              {validationData.validation_summary?.gaps?.enabled_but_not_implemented?.length || 0}
                            </StatNumber>
                            <StatHelpText>Registry enabled, not implemented</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                      <Card>
                        <CardBody>
                          <Stat>
                            <StatLabel>Registry Gaps</StatLabel>
                            <StatNumber color="blue.500">
                              {validationData.validation_summary?.gaps?.implemented_but_not_enabled?.length || 0}
                            </StatNumber>
                            <StatHelpText>Implemented but disabled</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                      <Card>
                        <CardBody>
                          <Stat>
                            <StatLabel>Missing Registry</StatLabel>
                            <StatNumber color="red.500">
                              {validationData.validation_summary?.gaps?.implemented_but_not_registered?.length || 0}
                            </StatNumber>
                            <StatHelpText>Not in registry at all</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                      <Card>
                        <CardBody>
                          <Stat>
                            <StatLabel>Actually Implemented</StatLabel>
                            <StatNumber color="green.500">
                              {validationData.validation_summary?.actually_implemented || 0}
                            </StatNumber>
                            <StatHelpText>Working capabilities</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                    </SimpleGrid>
                  )}

                  <Box width="100%" overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Capability</Th>
                          <Th>Type</Th>
                          <Th>Registry Status</Th>
                          <Th>Implementation Status</Th>
                          <Th>Reality Check</Th>
                          <Th>Handler Method</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {/* Component Capabilities */}
                        {Object.entries(aiCapabilities).map(([featureId, featureData]) =>
                          Object.entries(featureData.capabilities || {}).map(([capabilityId, capability]) => {
                            const realityStatus = getCapabilityRealityStatus(capabilityId);
                            return (
                              <Tr key={`component_${featureId}_${capabilityId}`}>
                                <Td>
                                  <VStack align="start" spacing={1}>
                                    <Text fontSize="sm" fontWeight="medium">{capabilityId}</Text>
                                    <Text fontSize="xs" color="gray.500">{featureData.feature_name}</Text>
                                  </VStack>
                                </Td>
                                <Td>
                                  <Badge colorScheme="blue" size="sm">Component</Badge>
                                </Td>
                                <Td>
                                  <Badge colorScheme={capability.enabled ? "green" : "red"}>
                                    {capability.enabled ? "Enabled" : "Disabled"}
                                  </Badge>
                                </Td>
                                <Td>
                                  <Badge colorScheme={realityStatus?.implemented ? "green" : "red"}>
                                    {realityStatus?.implemented ? "Implemented" : "Not Implemented"}
                                  </Badge>
                                </Td>
                                <Td>
                                  <Badge 
                                    colorScheme={realityStatus?.is_consistent ? "green" : "orange"}
                                    variant={realityStatus?.is_consistent ? "solid" : "outline"}
                                  >
                                    {realityStatus?.is_consistent ? "Consistent" : "Mismatch"}
                                  </Badge>
                                </Td>
                                <Td>
                                  <VStack align="start" spacing={1}>
                                    <Text fontSize="xs" color="gray.600">
                                      {realityStatus?.handler_method || "None"}
                                    </Text>
                                    {realityStatus?.implemented && (
                                      <Badge size="sm" colorScheme="blue" variant="outline">
                                        Modular
                                      </Badge>
                                    )}
                                  </VStack>
                                </Td>
                              </Tr>
                            );
                          })
                        )}
                        
                        {/* AI Reasoning Tools */}
                        {Object.entries(aiReasoningTools?.ai_reasoning_tools || {}).map(([toolId, tool]) => {
                          const realityStatus = getCapabilityRealityStatus(toolId);
                          return (
                            <Tr key={`ai_tool_${toolId}`} bg="purple.50">
                              <Td>
                                <VStack align="start" spacing={1}>
                                  <Text fontSize="sm" fontWeight="medium">{toolId}</Text>
                                  <Text fontSize="xs" color="gray.500">{tool.name}</Text>
                                </VStack>
                              </Td>
                              <Td>
                                <Badge colorScheme="purple" size="sm">AI Tool</Badge>
                              </Td>
                              <Td>
                                <Badge colorScheme="green">Always Enabled</Badge>
                              </Td>
                              <Td>
                                <Badge colorScheme="green">Backend API</Badge>
                              </Td>
                              <Td>
                                <Badge colorScheme="green" variant="solid">Consistent</Badge>
                              </Td>
                              <Td>
                                <VStack align="start" spacing={1}>
                                  <Text fontSize="xs" color="gray.600">
                                    {tool.endpoint}
                                  </Text>
                                  <Badge size="sm" colorScheme="purple" variant="outline">
                                    Backend
                                  </Badge>
                                </VStack>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>

                  {validationData?.validation_summary?.gaps && (
                    <VStack align="start" spacing={4} width="100%">
                      <Heading size="md">Gap Analysis</Heading>

                      {validationData.validation_summary.gaps.enabled_but_not_implemented?.length > 0 && (
                        <Card width="100%" borderColor="orange.200">
                          <CardBody>
                            <VStack align="start" spacing={3}>
                              <Text fontWeight="bold" color="orange.600">
                                ⚠️ Registry Enabled but Not Implemented ({validationData.validation_summary.gaps.enabled_but_not_implemented.length})
                              </Text>
                              {validationData.validation_summary.gaps.enabled_but_not_implemented.map((gap, index) => (
                                <Text key={index} fontSize="sm">
                                  • <Code fontSize="xs">{gap.capability}</Code> in <strong>{gap.feature}</strong>
                                </Text>
                              ))}
                            </VStack>
                          </CardBody>
                        </Card>
                      )}

                      {validationData.validation_summary.gaps.implemented_but_not_enabled?.length > 0 && (
                        <Card width="100%" borderColor="blue.200">
                          <CardBody>
                            <VStack align="start" spacing={3}>
                              <Text fontWeight="bold" color="blue.600">
                                💡 Implemented but Registry Disabled ({validationData.validation_summary.gaps.implemented_but_not_enabled.length})
                              </Text>
                              {validationData.validation_summary.gaps.implemented_but_not_enabled.map((gap, index) => (
                                <Text key={index} fontSize="sm">
                                  • <Code fontSize="xs">{gap.capability}</Code> in <strong>{gap.feature}</strong>
                                </Text>
                              ))}
                            </VStack>
                          </CardBody>
                        </Card>
                      )}

                      {validationData.validation_summary.gaps.implemented_but_not_registered?.length > 0 && (
                        <Card width="100%" borderColor="red.200">
                          <CardBody>
                            <VStack align="start" spacing={3}>
                              <Text fontWeight="bold" color="red.600">
                                🚨 Implemented but Missing from Registry ({validationData.validation_summary.gaps.implemented_but_not_registered.length})
                              </Text>
                              {validationData.validation_summary.gaps.implemented_but_not_registered.map((gap, index) => (
                                <Text key={index} fontSize="sm">
                                  • <Code fontSize="xs">{gap.capability}</Code> - Handler: <Code fontSize="xs">{gap.handler}</Code>
                                </Text>
                              ))}
                            </VStack>
                          </CardBody>
                        </Card>
                      )}
                    </VStack>
                  )}

                  <Button 
                    colorScheme="blue" 
                    onClick={loadValidationData}
                    isLoading={loadingValidation}
                    loadingText="Refreshing..."
                  >
                    Refresh Reality Check
                  </Button>
                </VStack>
              </TabPanel>

              {/* Statistics Tab */}
              <TabPanel>
                <VStack align="start" spacing={6} width="100%">
                  <Heading size="lg">Detailed Statistics</Heading>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} width="100%">
                    <Card>
                      <CardBody>
                        <VStack align="start" spacing={4}>
                          <Heading size="md">Feature Breakdown</Heading>
                          {Object.entries(aiCapabilities).map(([featureId, featureData]) => {
                            const capabilities = Object.values(featureData.capabilities || {});
                            const enabled = capabilities.filter(c => c.enabled).length;
                            const total = capabilities.length;

                            return (
                              <HStack key={featureId} justify="space-between" width="100%">
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="semibold">{featureData.feature_name}</Text>
                                  <Code fontSize="xs">{featureId}</Code>
                                </VStack>
                                <VStack align="end" spacing={0}>
                                  <Text fontSize="sm">{enabled}/{total}</Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {total > 0 ? Math.round(enabled / total * 100) : 0}%
                                  </Text>
                                </VStack>
                              </HStack>
                            );
                          })}
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card>
                      <CardBody>
                        <VStack align="start" spacing={4}>
                          <Heading size="md">System Health</Heading>
                          <VStack align="start" spacing={3} width="100%">
                            <HStack justify="space-between" width="100%">
                              <Text>Capability Coverage</Text>
                              <Badge colorScheme={stats.enabledPercentage > 50 ? "green" : "orange"}>
                                {stats.enabledPercentage}%
                              </Badge>
                            </HStack>
                            <HStack justify="space-between" width="100%">
                              <Text>Features with AI</Text>
                              <Badge colorScheme="blue">
                                {stats.featuresWithAI}/{Object.keys(features).length}
                              </Badge>
                            </HStack>
                            <HStack justify="space-between" width="100%">
                              <Text>System Status</Text>
                              <Badge colorScheme={stats.enabledCapabilities > 0 ? "green" : "red"}>
                                {stats.enabledCapabilities > 0 ? "Active" : "Inactive"}
                              </Badge>
                            </HStack>
                          </VStack>
                        </VStack>
                      </CardBody>
                    </Card>
                  </SimpleGrid>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>

          {/* Capability Details Modal */}
          <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                Capability Details: {capabilityDetails.id}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack align="start" spacing={4}>
                  <FormControl>
                    <FormLabel>Feature</FormLabel>
                    <Input value={capabilityDetails.featureName || ''} isReadOnly />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Capability ID</FormLabel>
                    <Input value={capabilityDetails.id || ''} isReadOnly />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Status</FormLabel>
                    <Badge colorScheme={capabilityDetails.enabled ? "green" : "red"}>
                      {capabilityDetails.enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea 
                      value={capabilityDetails.description || ''} 
                      isReadOnly 
                      rows={3}
                    />
                  </FormControl>

                  {capabilityDetails.usage && (
                    <FormControl>
                      <FormLabel>Usage Example</FormLabel>
                      <Textarea 
                        value={capabilityDetails.usage || ''} 
                        isReadOnly 
                        rows={2}
                      />
                    </FormControl>
                  )}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button colorScheme="blue" mr={3} onClick={onClose}>
                  Close
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </VStack>
      </Box>
    </Wrapper>
  );
};

export default AdminAICapabilities;